<template>
	<div :id="id" :style="{ width: width, height: height }" ></div>
</template>

<script setup lang="ts">
import { onBeforeMount, PropType, onMounted, watch, ref } from 'vue'
import type { ISearchedProperty } from '@/services/campayacore-api.service'
import useEventsBus from '@/services/eventbus.service'
import { debounce } from 'lodash-es'
import { storeToRefs } from 'pinia';
import { useFilterStore } from '@/store/filter.store'
import { openPropertyPageInNewTab } from '@/services/property.service';
const FILTER_STORE = useFilterStore();
const { filterObject: FILTER_OBJECT }: any = storeToRefs(FILTER_STORE);

const CLUSTER_MAXZOOM = 14
const CLUSTER_RADIUS = 18
const DEBOUNCE_TIME = 750

/**
 * @param {number[]} center - The center [lng, Lat] of the map and the marker position if a property array is not specified.
 * @param {string} height - The height of the map.
 * @param {string} width - The width of the map.
 * @param {ISearchedProperty[]} multipleHomes - The properties to display on the map.
 */
const props = defineProps({
	center: { type: Array as PropType<number[]>, required: true },
	height: { type: String, default: '400px' },
	width: { type: String, default: '100%' },
	zoom: { type: Number, default: 9 },
	multipleHomes: { type: Array as PropType<ISearchedProperty[]>, default: undefined },
	id: { type: String, default: 'map' },
	static: { type: Boolean, default: false },
	isMobile: { type: Boolean, default: false }
});

let map: any;
let isMapUpdating = false;
let currentMapZoom = 9;
let isZoomOutEvent = false;
let isDragEvent = false;

function redraw() {
	map.redraw();
}
defineExpose({
	redraw,
});


const emit = defineEmits(['marker-clicked'])
const { emit: _emit, bus } = useEventsBus()
const isResizeEvent = ref(false)
const isEventLoad = ref(false)
const previousPropertyNumber = ref(0)
const isReadyForZoomEvent = ref(false)
const isFirstRun = ref(true)
const hasSource = ref<boolean>(false)

function addMarker(map: any, lngLat: number[], price?: string) {
	let customMarker = createHtmlMarker(price);
	new maplibregl.Marker({ element: customMarker, anchor: 'bottom' })
		.setLngLat(lngLat)
		.addTo(map);
}


function createHtmlMarker(price?: string) {
	let customMarker = document.createElement("div");

	if (price) {
		// Use marker with price
		let markerLabel = document.createElement("span");
		markerLabel.className = "price-marker-label bg-white shadow-sm px-2 py-1 rounded-full border border-solid border-border-color text-sm font-bold text-text-color";
		markerLabel.innerHTML = price;
		customMarker.appendChild(markerLabel);

		customMarker.className = "price-marker";

	} else {
		// Use marker without price
		customMarker.className = 'custom-marker';
		customMarker.style.backgroundImage = 'url(src/images/icons/pin/single-pin-border-simple.webp)';
		customMarker.style.width = '38px';
		customMarker.style.height = '50px';
		customMarker.style.backgroundSize = 'cover';
	}

	return customMarker;
}

let isScriptLoaded = false;

onBeforeMount(() => {
	if (document.getElementById("maplibre-gl-js")) {
		isScriptLoaded = true;
		return; // Script already loaded
	}

	const script = document.createElement("script");
	script.src = "https://unpkg.com/maplibre-gl@3.6.2/dist/maplibre-gl.js"
	script.id = "maplibre-gl-js"
	document.head.appendChild(script)

	const link = document.createElement("link")
	link.rel = "stylesheet"
	link.href = 'https://unpkg.com/maplibre-gl@3.6.2/dist/maplibre-gl.css'
	document.head.appendChild(link)

	script.onload = () => {
		createMap();
		isScriptLoaded = true
	};

	script.onerror = () => {
		console.error('Failed to load Map Libre library.')
	}
})

onMounted(() => {
	window.addEventListener('resize', function () {
		// Set the flag to true when the window is resized
		isResizeEvent.value = true
	});

	if (isScriptLoaded) {
		createMap()
	}

	if (props.isMobile) {
		document.getElementById(props.id)?.scrollIntoView({block: 'end'})
	}
});

const isMapLoaded = () => {
	const loadState = (map.loaded() && map.areTilesLoaded()) || FILTER_OBJECT.value.mapLoadCounter == 1
	return loadState
}

/**
 * Emit event with the bounding box of the current map view
 */
const handleMoveEndEvent = () => {
	const bounds = map.getBounds()
	const ne = bounds.getNorthEast()
	const sw = bounds.getSouthWest()

	FILTER_OBJECT.value.mapZoom = map.getZoom()
	FILTER_OBJECT.value.mapCenter = map.getCenter()

	FILTER_OBJECT.value.mapLoadCounter = 1
	isEventLoad.value = true

	_emit('boundingbox-updated', { ne, sw })
};

const handleZoomEndEvent = () => {
	const bounds = map.getBounds();
	const ne = bounds.getNorthEast();
	const sw = bounds.getSouthWest();

	FILTER_OBJECT.value.mapLoadCounter = 1;
	isEventLoad.value = true;

	// Check if it's not the first run

	if (FILTER_OBJECT.value.isUserZoom) {
		_emit('boundingbox-updated', { ne, sw });
	}

	if (!isFirstRun.value) {
		FILTER_OBJECT.value.isUserZoom = true;
	}

	isFirstRun.value = false
}

// Debounce the event
// const debouncedHandleMoveEndEvent = debounce(handleMoveEndEvent, DEBOUNCE_TIME)
const debouncedHandleZoomEndEvent = debounce(handleZoomEndEvent, DEBOUNCE_TIME)

async function createMap() {
	map = new maplibregl.Map({
		container: props.id, // container id
		style: 'https://campaya.mapinfo.viamap-gms.net/v1/style.json?token=eyJkcGZ4IjogImNhbXBheWEiLCAicmVmIjogImNhbXBheWEgc2l0ZTEiLCAicGFyIjogImNhbXBheWEiLCAicHJpdnMiOiAicmdkamhjNy9kaXRFQTNCWiJ9.At/6XdsTaASNJKkCJjrwkJeDMfKYT1sCWdRgJMa+m5JJ38DNxhXtlu0LBIQWT/Xg30Gq1I4fj5r3x0kZVshF3Q', // style URL
		center: props.center || [0, 40], // starting position [lng, lat]
		zoom: props.zoom, // starting zoom
		locale: document.documentElement.lang,
		interactive: !props.static,
	})

	map.addControl(new maplibregl.NavigationControl(), 'bottom-right')


	addImagesToMap(map)


	if (props.multipleHomes) {
		if (props.multipleHomes.length > 0) {
			map.fitBounds(getBounds(props.multipleHomes), { padding: 50 })
		}

		addClusters(map, convertMultipleHomesToGeoJson(props.multipleHomes))
	} else {
		addMarker(map, props.center)
	}

	map.on('load', () => {
		setLanguage(document.documentElement.lang.slice(0, 2))

		map.on('styleimagemissing', () => {
			addImagesToMap(map)
		})

		/**
	   * When the map is moved, do a debounced emit to make a new search
	   * based on the bounding box of the current map view
	   */
		map.on('dragend', debouncedHandleZoomEndEvent)
		map.on('dragend', () => {
			isDragEvent = true
		});
		map.on('zoomend', debouncedHandleZoomEndEvent);
		map.on('zoomend', () => {
			isZoomOutEvent = isZoomOutEvent || currentMapZoom > map.getZoom()
			currentMapZoom = map.getZoom()
		})
	});

	currentMapZoom = map.getZoom()



	// Wait for attribution to be added to DOM
	const attribution = await waitForElement(`#${props.id} .maplibregl-control-container .maplibregl-ctrl-bottom-right .maplibregl-compact-show summary`);
	(<HTMLElement>attribution)?.click();
	return map;
}

function waitForElement(selector: string) {
	return new Promise(resolve => {
		if (document.querySelector(selector)) {
			return resolve(document.querySelector(selector));
		}

		const observer = new MutationObserver(_mutations => {
			const element = document.querySelector(selector);
			if (element) {
				observer.disconnect();
				resolve(element);
			}
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true
		});
	});
}

function setLanguage(lang) {
	let style = map.getStyle();
	style.layers.forEach(({ id }) => {
		if (id === "place_label_city_town_medium" || id === 'place_label_city_big' || id === 'country_label') {
			map.setLayoutProperty(id, 'text-field', `{name:${lang}}`);
		}
	})
}

function getBounds(multipleHomes) {
	// Note: could be calculated by the backend
	const bounds = multipleHomes.reduce((acc, home) => {
		return [
			[
				Math.max(acc[0][0], home.lon),
				Math.max(acc[0][1], home.lat)
			],
			[
				Math.min(acc[1][0], home.lon),
				Math.min(acc[1][1], home.lat)
			]
		]
	}, [[-Infinity, -Infinity], [Infinity, Infinity]]);

	const sw = new maplibregl.LngLat(bounds[1][0], bounds[1][1]);
	const ne = new maplibregl.LngLat(bounds[0][0], bounds[0][1]);
	return new maplibregl.LngLatBounds(sw, ne);
}

const addClusters = (map, geojson) => {
	map.on('load', () => {
		loadGeoJson(map, geojson);

		addClusterPinLayer(map)
		addClusterTextLayer(map)
		addSinglePinLayer(map)



		map.on('click', 'clusters', function (e) {
			var features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
			var clusterId = features[0].properties.cluster_id;
			map.getSource('properties').getClusterExpansionZoom(clusterId, function (err, zoom) {
				if (err)
					return;

				//Sometimes clusterexpansionzoom doesn't zoom in, so if map zoom and expansion zoom is the same, then always zoom
				if (map.getZoom() === zoom)
					zoom++;

				map.easeTo({
					center: features[0].geometry.coordinates,
					zoom: zoom
				});
				currentMapZoom = map.getZoom()
			});
		});
		map.on('click', 'unclustered-point', (e) => {
			const features = map.queryRenderedFeatures(e.point, { layers: ['unclustered-point'] });
			const propertyId = features[0].properties.id;
			if (e.originalEvent.ctrlKey) {
				return openPropertyPageInNewTab(propertyId);
			}
			emit('marker-clicked', propertyId);
		})

		map.on('mousemove', 'unclustered-point', function (e) {
			map.getCanvas().style.cursor = 'pointer';
		});
		map.on('mousemove', 'clusters', function (e) {
			map.getCanvas().style.cursor = 'pointer';
		});
		map.on('mouseleave', 'unclustered-point', function (e) {
			map.getCanvas().style.cursor = '';
		});
		map.on('mouseleave', 'clusters', function (e) {
			map.getCanvas().style.cursor = '';
		});
	});
}

function addImagesToMap(map) {
	map.loadImage('/src/images/icons/pin/single-pin-border-simple.webp', (error, image) => {
		if (error) return console.error(error);
		if (!map.hasImage('single-pin')) {
			map.addImage('single-pin', image);
		}
	});
	map.loadImage('/src/images/icons/pin/cluster-pin-border-simple.webp', (error, image) => {
		if (error) return console.error(error);
		if (!map.hasImage('cluster-pin')) {
			map.addImage('cluster-pin', image);
		}
	});
}

function loadGeoJson(map, geojson) {
	if (!map.getSource('properties')) {
		map.addSource('properties', {
			type: 'geojson',
			data: geojson,
			cluster: true,
			clusterMaxZoom: CLUSTER_MAXZOOM, // Max zoom to cluster points on
			clusterRadius: CLUSTER_RADIUS // Radius of each cluster when clustering points (defaults to 50)
		})
		hasSource.value = true
	}
}

function addClusterPinLayer(map) {
	if (map.getLayer('clusters')) {
		map.removeLayer('clusters');
	}

	map.addLayer({
		id: "clusters",
		type: "symbol",
		interactive: true,
		source: "properties",
		filter: ["has", "point_count"],
		layout: {
			"icon-image": "cluster-pin",
			"icon-allow-overlap": true,
			"text-allow-overlap": true,
			"icon-size": [ //Change size, depending on number
				"step",
				["get", "point_count"],
				1,
				10, //when more than 10
				1.1,
				100, //When more than 100
				1.2,
				1000, //when more than 1000
				1.3
			],
			"symbol-sort-key": 1
		}
	});
}

function addClusterTextLayer(map) {
	if (map.getLayer('cluster-count')) {
		map.removeLayer('cluster-count');
	}

	map.addLayer({
		id: "cluster-count",
		type: "symbol",
		source: "properties",
		filter: ["has", "point_count"],
		layout: {
			"text-field": "{point_count_abbreviated}",
			"text-font": ["OpenSans-SemiBold"],
			"text-size": 12,
			"text-allow-overlap": true,
			"symbol-sort-key": 2
		},
		paint: {
			"text-color": "white"
		}
	});
}

function addSinglePinLayer(map) {
	if (map.getLayer('unclustered-point')) {
		map.removeLayer('unclustered-point');
	}

	map.addLayer({
		id: "unclustered-point",
		type: "symbol",
		interactive: true,
		source: "properties",
		filter: ["!", ["has", "point_count"]],
		layout: {
			"icon-image": "single-pin",
			"icon-anchor": "bottom",
			"icon-allow-overlap": true,
			"text-allow-overlap": true,
			"symbol-sort-key": 3
		}
	});
}

// Note: could be calculated by the backend
function convertMultipleHomesToGeoJson(multipleHomes: ISearchedProperty[]) {
	return {
		"type": "FeatureCollection",
		"features": multipleHomes.map((home) => {
			return {
				"type": "Feature",
				"properties": {
					"id": home.id
				},
				"geometry": {
					"type": "Point",
					"coordinates": [home.lon, home.lat]
				}
			}
		})
	}
}




const updateMapFromWatch = async (shouldAddToMap: boolean) => {

	if (!map) return;
	if (FILTER_OBJECT.value.mapLoadCounter != 2) return;

	const source = map.getSource("properties");

	if (!source) {
		addClusters(map, convertMultipleHomesToGeoJson(props.multipleHomes));
		console.error('No map source');
	}

	const newGeoJson = convertMultipleHomesToGeoJson(props.multipleHomes);
	const currentGeoJson = source._data; // Access the current data directly from the mapbox-gl source

	if (JSON.stringify(newGeoJson) !== JSON.stringify(currentGeoJson)) {
		if (shouldAddToMap) {
			map.getSource("properties").setData(newGeoJson);
		}

		if (FILTER_OBJECT.value.isUserZoom) return;

		// FILTER_OBJECT.value.mapLoadCounter = 2 means that this is not the initialLoad search request
		if (props.multipleHomes.length > 0 && FILTER_OBJECT.value.mapLoadCounter == 2) {
			map.fitBounds(getBounds(props.multipleHomes), { padding: 50 });
		}
	} else {
		if (FILTER_OBJECT.value.isUserZoom) return;
		map.fitBounds(getBounds(props.multipleHomes), { padding: 50 });
	}

	currentMapZoom = map.getZoom()
}



// Keeps the map updated
watch(
	[() => props.multipleHomes, () => hasSource.value],
	async (newMultihomeValue, oldMultihomeValue) => {
		const shouldAddToMap = props.multipleHomes != undefined &&
							  (props.multipleHomes?.length >= 10000 ||
							  (FILTER_OBJECT.value.mapLoadCounter == 2 &&
							  (!FILTER_OBJECT.value.isUserZoom || isZoomOutEvent || isDragEvent)))
		if (!hasSource.value) { return }

		isZoomOutEvent = false;
		isDragEvent = false;

		updateMapFromWatch(shouldAddToMap);
		// if (!isEventLoad.value) {
		//   updateMapFromWatch(true);
		//   return isEventLoad.value = false
		// }

		// updateMapFromWatch(false);
	},
	{ deep: true })
</script>

<style lang="scss">

@media (min-width: 1024px) {
	.maplibregl-ctrl maplibregl-ctrl-attrib {
		padding-right: 1rem;
		border-top-left-radius: 9999px;
		border-bottom-left-radius: 9999px;
	}
}

</style>